import type { Periode } from './periodePresets';

/**
 * Story 31.2, AC-7 — het venster waar de vergelijking uit komt.
 *
 * Even lang als de gekozen periode en direct ervóór: voor "1 tot en met 30 augustus" is dat
 * "2 tot en met 31 juli". Geen overlap en geen gat, want beide zouden het verschil vertekenen —
 * een dag die dubbel telt maakt de vorige periode zwaarder dan hij was.
 *
 * Alles in HELE DAGEN met de tot-datum meegeteld, precies zoals het scherm de periode toont. De
 * serverlaag vertaalt dat verderop naar het half-open venster van de bron.
 */
export interface VorigePeriode extends Periode {
  /** De lengte in dagen, met beide grensdagen meegeteld. Het scherm noemt hem in de vergelijking. */
  dagen: number;
}

/** Een kale datum `JJJJ-MM-DD` als tijdstip op UTC-middernacht, of `null` als het er geen is. */
function alsUtc(datum: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datum)) return null;
  const [jaar, maand, dag] = datum.split('-').map(Number) as [number, number, number];
  const tijd = Date.UTC(jaar, maand - 1, dag);
  const terug = new Date(tijd);
  // Een datum die niet bestaat (`2026-02-31`) rolt door naar de volgende maand. Dat mag hier geen
  // stilzwijgend andere periode opleveren.
  if (terug.getUTCFullYear() !== jaar || terug.getUTCMonth() !== maand - 1 || terug.getUTCDate() !== dag) return null;
  return tijd;
}

function alsDatum(tijd: number): string {
  return new Date(tijd).toISOString().slice(0, 10);
}

const ETMAAL = 24 * 60 * 60 * 1000;

/**
 * De even lange periode die direct vóór de gekozen periode ligt.
 *
 * Gerekend op UTC-middernacht en niet op de lokale klok: twee keer per jaar duurt een lokaal
 * etmaal 23 of 25 uur, en dan komt "dertig dagen terug" op de verkeerde dag uit. Op UTC bestaat
 * die sprong niet, en omdat er alleen met kale datums in en uit gegaan wordt verandert er verder
 * niets aan wat de lezer ziet.
 *
 * Geeft `null` zodra er geen bruikbare periode is — geen datums, een onmogelijke datum, of een
 * tot-datum die vóór de van-datum ligt. Het scherm doet dan gewoon geen tweede aanroep.
 */
export function vorigePeriode(periode: { from?: string; to?: string } | null): VorigePeriode | null {
  if (periode == null || !periode.from || !periode.to) return null;

  const van = alsUtc(periode.from);
  const tot = alsUtc(periode.to);
  if (van === null || tot === null || tot < van) return null;

  const dagen = Math.round((tot - van) / ETMAAL) + 1;
  const vorigeTot = van - ETMAAL;
  const vorigeVan = vorigeTot - (dagen - 1) * ETMAAL;

  return { from: alsDatum(vorigeVan), to: alsDatum(vorigeTot), dagen };
}
